/**
 * Navigation and translation contracts shared by every rewards admin screen.
 *
 * This package never imports react-router or next — the host application injects
 * navigation behavior through callbacks and an optional translator. Permission
 * gating is also host-driven: pass the actor's granted permission strings and
 * actions the actor cannot perform are hidden.
 */

/** Translator function. Defaults to identity when the host omits it. */
export type Translator = (key: string) => string;

/**
 * Turns a dotted i18n key into a readable English label by humanizing its last
 * segment (e.g. `rewards.simulator.purchase_type` -> "Purchase type"). Used as
 * the fallback so screens read naturally before a real translator is wired up.
 */
export function humanizeKey(key: string): string {
  const last = key.split(".").pop() ?? key;
  const words = last.replace(/_/g, " ").trim();
  if (!words) return key;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Default translator used when the host omits one. Rather than echoing the raw
 * key, it humanizes it so the admin screens are readable out of the box. A host
 * may pass a real `t` (e.g. react-i18next) to override with translated copy.
 */
export const identityTranslator: Translator = (key) => humanizeKey(key);

/** The high-level sections the rewards admin module is divided into. */
export type RewardsAdminSection =
  | "dashboard"
  | "programs"
  | "tiers"
  | "rules"
  | "simulator"
  | "members"
  | "catalog"
  | "redemptions"
  | "audit";

/**
 * Navigation callbacks injected by the host. Every screen accepts a subset of
 * these; unused callbacks are simply omitted by the host.
 */
export interface IRewardsNavHandlers {
  /** Navigate to a top-level section. */
  onNavigate?: (section: RewardsAdminSection) => void;
  /** Open a program (detail / edit). */
  onOpenProgram?: (programId: string) => void;
  /** Open a rule (detail / edit). */
  onOpenRule?: (ruleId: string) => void;
  /** Open a catalog item (detail / edit). */
  onOpenCatalogItem?: (itemId: string) => void;
  /** Open a member detail screen. */
  onOpenMember?: (memberId: string) => void;
  /** Open a redemption detail. */
  onOpenRedemption?: (redemptionId: string) => void;
  /** Generic "go back" / cancel handler for form screens. */
  onBack?: () => void;
}

/** Props shared by every screen: a translator and the actor's permissions. */
export interface IRewardsScreenBaseProps {
  /** Optional translator; defaults to identity. */
  t?: Translator;
  /** The actor's granted permission strings (for hiding unauthorized actions). */
  permissions?: readonly string[];
}
